import React, { useState, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import { useExpenseReports, useLandlords } from "services/hooks/rentals";
import * as XLSX from "xlsx";
import { months, years, formatCurrency } from "./constants";

const formatDate = (value) => {
  if (!value) return "";
  try {
    const d = new Date(value);
    return isNaN(d.getTime()) ? value : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return value;
  }
};

const buildExpensesExcelAndDownload = (expenses, totalExpenses, count, filters, landlords, formatCurrencyFn) => {
  const data = Array.isArray(expenses) ? expenses : [];
  const landlordName = filters.landlord_id
    ? (landlords?.find((l) => l.id === Number(filters.landlord_id))?.business_name ?? `Landlord #${filters.landlord_id}`)
    : "All Landlords";

  const rows = [
    ["Expenses Report"],
    [],
    ["Report parameters"],
    ["Landlord", "Month", "Year"],
    [landlordName, filters.month || "All Months", filters.year || "All Years"],
    [],
    ["Summary"],
    ["Total Expenses", "Count"],
    [formatCurrencyFn(totalExpenses), count],
    [],
    ["Landlord / Business Name", "Rental Name", "Description", "Expense Type", "Amount", "Date (effective_on)", "Added By"],
  ];

  data.forEach((row) => {
    const landlordNameCell = row.related_landlord_rental?.related_landlord?.business_name ?? "N/A";
    const rentalNameCell = row.related_landlord_rental?.rental_name ?? "";
    const expenseType = row.related_expense_detail?.expense_type ?? row.related_expense_detail?.name ?? "";
    const addedBy = row.added_by ? `${row.added_by.first_name || ""} ${row.added_by.last_name || ""}`.trim() : "";
    rows.push([
      landlordNameCell,
      rentalNameCell,
      row.description ?? "",
      expenseType,
      typeof row.amount === "number" ? formatCurrencyFn(row.amount) : (row.amount ?? ""),
      row.effective_on ? formatDate(row.effective_on) : "",
      addedBy,
    ]);
  });

  const ws = XLSX.utils.aoa_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Expenses Report");
  const fileName = `expenses-report-${filters.landlord_id || "all"}-${filters.month || "all"}-${filters.year || "all"}.xlsx`;
  XLSX.writeFile(wb, fileName);
};

const ExpensesReport = () => {
  const [filters, setFilters] = useState({
    landlord_id: "",
    month: "",
    year: "",
  });

  const { expenses, totalExpenses, count, isLoadingExpenses, error } = useExpenseReports(filters);
  const { landlords } = useLandlords();

  const handleFilterChange = useCallback((field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleDownloadExcel = () => {
    buildExpensesExcelAndDownload(expenses, totalExpenses, count, filters, landlords, formatCurrency);
  };

  const canDownloadExcel = count > 0 || (expenses && expenses.length > 0);

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Report Filters
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Filter landlord expenses by landlord, month and year. Use &quot;Download Excel&quot; to export the current view.
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth>
              <InputLabel>Landlord</InputLabel>
              <Select value={filters.landlord_id} onChange={(e) => handleFilterChange("landlord_id", e.target.value)} label="Landlord">
                <MenuItem value="">All Landlords</MenuItem>
                {(landlords || []).map((landlord) => (
                  <MenuItem key={landlord.id} value={landlord.id}>
                    {landlord.business_name ?? landlord.rental_name ?? `Landlord #${landlord.id}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Month</InputLabel>
              <Select value={filters.month} onChange={(e) => handleFilterChange("month", e.target.value)} label="Month">
                <MenuItem value="">All Months</MenuItem>
                {months.map((month) => (
                  <MenuItem key={month} value={month}>{month}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth>
              <InputLabel>Year</InputLabel>
              <Select value={filters.year} onChange={(e) => handleFilterChange("year", e.target.value)} label="Year">
                <MenuItem value="">All Years</MenuItem>
                {years.map((year) => (
                  <MenuItem key={year} value={year}>{year}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <Button variant="contained" onClick={handleDownloadExcel} disabled={!canDownloadExcel} sx={{ mt: 1 }}>
              Download Excel
            </Button>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 1 }}>
              Expenses report updates when you change filters. Download Excel to export the data currently on screen.
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {isLoadingExpenses && (
        <Alert severity="info" sx={{ mb: 2 }}>Fetching records…</Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>Error loading expenses report. Please try again.</Alert>
      )}

      {(count > 0 || totalExpenses !== 0) && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Total Expenses</Typography>
                <Typography variant="h4">{formatCurrency(totalExpenses)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Count</Typography>
                <Typography variant="h4">{count}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {expenses && expenses.length > 0 && (
        <Paper sx={{ mt: 3 }}>
          <Typography variant="h6" sx={{ p: 2 }}>Expenses Data ({expenses.length} records)</Typography>
          <Divider />
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Landlord / Business Name</TableCell>
                  <TableCell>Rental Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Expense Type</TableCell>
                  <TableCell align="right">Amount</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Added By</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {expenses.map((row, index) => (
                  <TableRow key={row.id ?? index} hover>
                    <TableCell>{row.related_landlord_rental?.related_landlord?.business_name ?? "N/A"}</TableCell>
                    <TableCell>{row.related_landlord_rental?.rental_name ?? ""}</TableCell>
                    <TableCell>{row.description ?? ""}</TableCell>
                    <TableCell>{row.related_expense_detail?.expense_type ?? row.related_expense_detail?.name ?? ""}</TableCell>
                    <TableCell align="right">{formatCurrency(row.amount)}</TableCell>
                    <TableCell>{formatDate(row.effective_on)}</TableCell>
                    <TableCell>{row.added_by ? `${row.added_by.first_name || ""} ${row.added_by.last_name || ""}`.trim() : ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {expenses && expenses.length === 0 && !isLoadingExpenses && (
        <Paper sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="h6" color="textSecondary">No expenses for the selected filters</Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ExpensesReport;
